import type { ApiResponse, ListQuery, PageResponse } from '@core/types'
import { humanizeErrorMessage } from './humanizeErrorMessage'

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/factory-api'

/** 获取存储的 JWT token */
function getToken(): string | null {
  return localStorage.getItem('token')
}

/** 设置 JWT token */
function setToken(token: string) {
  localStorage.setItem('token', token)
}

/** 清除 JWT token */
function clearToken() {
  localStorage.removeItem('token')
}

/** API 请求封装 */
async function apiRequest<T>(
  path: string,
  options: {
    method?: string
    body?: unknown
    auth?: boolean
  } = {},
): Promise<ApiResponse<T>> {
  const { method = 'GET', body, auth = true } = options
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (auth) {
    const token = getToken()
    if (token) {
      headers['Authorization'] = token
    }
  }

  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    })

    // 未授权 → 清除 token
    if (response.status === 401) {
      clearToken()
      window.location.href = '/login'
      return {
        code: 401,
        message: humanizeErrorMessage('Unauthorized', 401),
        data: null as T,
      }
    }

    const json = await response.json()
    // 后端格式: { code: 200, msg: "成功", data: ... }
    const code = json.code
    const rawMessage = json.msg || json.message || ''

    return {
      code: json.code,
      message: code === 200 ? rawMessage : humanizeErrorMessage(rawMessage, response.status),
      data: json.data,
    }
  } catch (error) {
    return {
      code: -1,
      message: humanizeErrorMessage(
        error instanceof Error ? error.message : 'Network error',
      ),
      data: null as T,
    }
  }
}

/** 从后端分页查询结果构建 PageResponse */
function toPageResponse<T>(
  res: ApiResponse<{ total: number; list: T[] }>,
  query: { page: number; pageSize: number },
): PageResponse<T> {
  if (res.code === 200 && res.data) {
    return {
      list: res.data.list || [],
      total: res.data.total || 0,
      page: query.page,
      pageSize: query.pageSize,
    }
  }
  return { list: [], total: 0, page: query.page, pageSize: query.pageSize }
}

/** Assert API response is successful (code 200), throw otherwise */
function assertOk<T>(res: ApiResponse<T>): T {
  if (res.code !== 200) {
    throw new Error(humanizeErrorMessage(res.message))
  }
  return res.data
}

export { apiRequest, getToken, setToken, clearToken, toPageResponse, assertOk }
